import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import AdminSidebar from "./components/AdminSidebar";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import CreateQR from "./pages/CreateQR";
import MyQRCodes from "./pages/MyQRCodes";
import Analytics from "./pages/Analytics";
import AdminDashboard from "./pages/AdminDashboard";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminUsers from "./pages/AdminUsers";
import AdminQRCodes from "./pages/AdminQRCodes";


/* =========================
   Common Footer
========================= */
function AppFooter() {
  return (
    <footer className="app-footer">
      © 2026 myG IT Projects. All rights reserved.
    </footer>
  );
}


/* =========================
   Normal User Layout
========================= */
function AppLayout({ children }) {
  return (
    <div>
      <Navbar />

      <div className="app-shell">
        <Sidebar />

        <div className="main-content">
          <div className="page-content">
            {children}
          </div>

          <AppFooter />
        </div>
      </div>
    </div>
  );
}


/* =========================
   Admin Layout
========================= */
function AdminLayout({ children }) {
  return (
    <div>
      <Navbar />

      <div className="app-shell">
        <AdminSidebar />

        <div className="main-content">
          <div className="page-content">
            {children}
          </div>

          <AppFooter />
        </div>
      </div>
    </div>
  );
}


/* =========================
   Role Aware Layout
   Used for Create/Edit QR
========================= */
function RoleAwareLayout({ children }) {
  const { isAdmin } = useAuth();

  return isAdmin ? (
    <AdminLayout>{children}</AdminLayout>
  ) : (
    <AppLayout>{children}</AppLayout>
  );
}


/* =========================
   Default Redirect
========================= */
function DefaultRedirect() {
  const isAuthenticated = !!localStorage.getItem("access_token");
  const { isAdmin, isManager } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Navigate
      to={(isAdmin || isManager) ? "/admin" : "/dashboard"}
      replace
    />
  );
}


/* =========================
   Main App
========================= */
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>

          {/* =========================
              Login
          ========================= */}
          <Route
            path="/login"
            element={<Login />}
          />

          {/* Register currently disabled */}
          {/*
          <Route
            path="/register"
            element={<Register />}
          />
          */}


          {/* =========================
              User Routes
          ========================= */}

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/create"
            element={
              <ProtectedRoute>
                <RoleAwareLayout>
                  <CreateQR />
                </RoleAwareLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/edit/:id"
            element={
              <ProtectedRoute>
                <RoleAwareLayout>
                  <CreateQR />
                </RoleAwareLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-qr-codes"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <MyQRCodes />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Analytics />
                </AppLayout>
              </ProtectedRoute>
            }
          />


          {/* =========================
              Admin Routes
          ========================= */}

          <Route
            path="/admin"
            element={
              <AdminRoute allowManager>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </AdminRoute>
            }
          />

          <Route
            path="/admin/analytics"
            element={
              <AdminRoute allowManager>
                <AdminLayout>
                  <AdminAnalytics />
                </AdminLayout>
              </AdminRoute>
            }
          />

          <Route
            path="/admin/qrcodes"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminQRCodes />
                </AdminLayout>
              </AdminRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminUsers />
                </AdminLayout>
              </AdminRoute>
            }
          />


          {/* =========================
              Default / Fallback
          ========================= */}

          <Route
            path="*"
            element={<DefaultRedirect />}
          />

        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}